import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  useGetPaymentApiV1PaymentsPaymentIdGet,
  useUpdatePaymentApiV1PaymentsPaymentIdPatch,
} from '#/shared/api/generated/billing/theCargoBilling'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'

const PAYMENT_LIST_QUERY_PREFIX = '/api/v1/payments'

export interface PaymentComment {
  /** Saved note from the payment detail — the list item does not carry it. */
  note: string
  isLoading: boolean
  isSaving: boolean
  save: (note: string) => Promise<boolean>
}

export function usePaymentComment(
  paymentId: string,
  enabled: boolean,
): PaymentComment {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const detailQuery = useGetPaymentApiV1PaymentsPaymentIdGet(paymentId, {
    query: { enabled: enabled && Boolean(paymentId) },
  })
  const mutation = useUpdatePaymentApiV1PaymentsPaymentIdPatch()

  const save = async (note: string) => {
    try {
      // PATCH only the note — sending money fields would recompute totals.
      await mutation.mutateAsync({ paymentId, data: { note } })
      queryClient.invalidateQueries({ queryKey: [PAYMENT_LIST_QUERY_PREFIX] })
      toast.success(t('orderDetails.comment.payment.commentDialog.saveSuccess'))
      return true
    } catch (err) {
      toast.error(
        getApiErrorDetail(err) ??
          t('orderDetails.comment.payment.commentDialog.saveError'),
      )
      return false
    }
  }

  return {
    note: detailQuery.data?.data.note ?? '',
    isLoading: detailQuery.isLoading && enabled,
    isSaving: mutation.isPending,
    save,
  }
}
